import * as React from "react";
import {observer} from "mobx-react";
import {autorun, configure, reaction, runInAction} from "mobx"; configure({ enforceActions: "never" });

import "./app.scss";

import {AppConfiguration} from "../../core/config/app-configuration";
import {ModuleConfiguration} from "../../core/config/module-configuration";
import {MapViewerConfiguration} from "../../config/map-viewer-configuration";

import {ErrorBoundary} from "../../core/components/error-boundary/error-boundary";
import esriConfig from "@arcgis/core/config";

import {MapState} from "../../core/map-state";
import {UiState} from "../../core/ui-state";
import {AppState} from "./app-mobx";

import {Header} from "./header/header";
import {Footer} from "./footer/footer";
import {Navigation} from "./navigation/navigation";
import {MobileNavigation} from "./navigation/mobile-navigation";
import {Breadcrumbs} from "./navigation/breadcrumbs";
import {ObjektwesenFooter} from "./footer/objektwesen-footer";
import Maintenance from "../../core/components/maintenance/maintenance";

import {GeoProxy} from "../../core/geo-proxy";
import {Item, MessageProps, Sidebar as SemanticSidebar} from "semantic-ui-react";

import {TopicConfiguration} from "../../config/topic-configuration";
import {MessageConfiguration} from "../../core/config/message-configuration";
import {MessageBox} from "../../core/components/message-box/message-box";
import MessageBlock from "../../core/components/message-block/message-block";
import MessageStartup from "../../core/components/message-startup/message-startup";
import {Popup} from "../../core/components/popup/popup";
import IdentityManager from "@arcgis/core/identity/IdentityManager";
import {NavigateFunction} from "react-router";
import CookieBanner from "../../core/components/cookie-banner/cookie-banner";
import EsriCompass from "@arcgis/core/widgets/Compass";
import EsriLocate from "@arcgis/core/widgets/Locate";
import Viewpoint from "@arcgis/core/Viewpoint";
import {ObjectResult} from "./content/object-result";
import {Kontakt} from "./content/page/kontakt";
import {Faq} from "./content/page/faq";
import {Aktuell} from "./content/page/aktuell";
import {Ueber} from "./content/page/ueber";
import {Aside} from "./content/aside/aside";
import {ExtendedSearchViewModel, Search} from "../../core/components/search/search";
import {NavigationConfiguration} from "../../config/navigation-configuration";
import * as arcade from "@arcgis/core/arcade";
import {ObjektluStore} from "../../stores/objektlu-store-mobx";
import {MapViewerStore} from "../../stores/map-viewer-store-mobx";
import {AppStoreProvider} from "../../core/stores/abstract-app-store";

configure({ enforceActions: "never" });

/* Lazy load Webmap to show loader while importing */
const WebMap = React.lazy(() =>
    import("../../core/geo").then((module) => ({ default: module.WebMap }))
);

export interface AppProps {
    config: AppConfiguration;
    moduleConfig: ModuleConfiguration;
    appStore: AppState;
    location: Location;
    navigate: NavigateFunction;
    params: any;
    searchParams: URLSearchParams;
    setSearchParams: () => void;
}

/**
 * Anwendungsklasse
 */
@observer
export class App extends React.Component<AppProps, any> {
    mapViewerStore: MapViewerStore;

    autorunDisposers: any[] = [];
    appStore: AppState;
    uiStore: UiState;
    mapStore: MapState;
    oStore: ObjektluStore;

    mapViewerConfig: MapViewerConfiguration;
    appConfig: AppConfiguration;

    appUiRef = React.createRef<HTMLDivElement>();

    defaultScale?: number;
    stopBrowserNavigationListener: any;
    focusArgs: number[] = [];

    constructor(props: AppProps) {
        super(props);
        this.appStore = this.props.appStore;
        this.uiStore = new UiState();
        this.mapStore = new MapState();
        this.state = {
            northFacing: true,
            alertMessage: [],
            startupMessage: [],
            addClass: []
        };

        this.uiStore.setUiSize(window.innerWidth, window.innerHeight);

        this.mapViewerConfig = this.props.moduleConfig as MapViewerConfiguration;
        this.appConfig = props.config as AppConfiguration;
        this.defaultScale = this.appConfig.mapView.scale;

        this.oStore = new ObjektluStore(
            this.appConfig,
            this.mapViewerConfig.thematicInspector,
            this.mapStore,
            this.mapViewerConfig.objektluLayerList);
        // initialize the core application store
        this.mapViewerStore = new MapViewerStore({
            appConfig: this.appConfig,
            mapViewerConfig: this.mapViewerConfig,
            appStore: this.appStore,
            mapStore: this.mapStore,
            uiStore: this.uiStore,
            oStore: this.oStore
        });

        // configure proxy page
        const geoProxy = new GeoProxy(this.appConfig.proxy);
        geoProxy.loadRules();
        esriConfig.request.proxyUrl = this.appConfig.proxy.url;

        // configure assets path
        if (this.appConfig.assetsPath) {
            esriConfig.assetsPath = this.appConfig.assetsPath;
        }
        // configure fonts URL
        if (this.appConfig.fontsUrl) {
            esriConfig.fontsUrl = this.appConfig.fontsUrl;
        }
        // configure GeometryService URL
        if (this.appConfig.geometryServiceUrl) {
            esriConfig.geometryServiceUrl = this.appConfig.geometryServiceUrl;
        }
        // configure portal
        if (this.appConfig.portalUrl) {
            esriConfig.portalUrl = this.appConfig.portalUrl;
            // Esri Anmeldedialog unterdrücken
            // eslint-disable-next-line max-len
            // https://community.esri.com/t5/arcgis-api-for-javascript-questions/detect-or-prevent-the-arcgis-authentication-dialog/td-p/1047565
            IdentityManager.on("dialog-create", () => {
                // eslint-disable-next-line no-console
                console.warn("Ungenügende Berechtigung (IdentityManager dialog-create)");
                IdentityManager.dialog.destroy();
            });
        } else {
            // eslint-disable-next-line no-console
            console.warn("No portal connection defined.");
        }
        this.mapStore.setAppConfig(this.appConfig);
        this.appStore.setTopic(
            {id:"objektwesen", name: this.mapViewerConfig.appName,
                label: this.mapViewerConfig.appName, routes: ["/"]} as TopicConfiguration);

        this.handleMapLoad = this.handleMapLoad.bind(this);
        this.handleNavigation = this.handleNavigation.bind(this);
        this.handleBrowserNavigation = this.handleBrowserNavigation.bind(this);
        this.handleResize = this.handleResize.bind(this);

        // Zufälliger Extent aus Bookmarks, falls weder center noch extent definiert wurde
        if(!this.appConfig.mapView.center && !this.appConfig.mapView.extent
            && !this.appConfig.mapView.viewpoint) {
            const {bookmarks} = this.mapViewerConfig;
            const bookmark = bookmarks[Math.floor(Math.random() * bookmarks.length)];
            if(bookmark?.viewpoint) {
                delete this.appConfig.mapView.center;
                delete (this.appConfig.mapView as any).scale;
                delete this.appConfig.mapView.zoom;
                this.appConfig.mapView.viewpoint = bookmark.viewpoint;
                if("scale" in this.appConfig.mapView) {
                    this.appConfig.mapView.viewpoint.scale = this.appConfig.mapView.scale;
                }
            }
        }
    }

    /**
     * Extract route from url and find topic by rout
     * Set topic when route matches a topic
     */
    setTopicFromUrl(): void {
        if (this.props.params && this.props.params.subtopic) {
            const topicRoute = this.props.params.subtopic;
            // get topicId from url and set to mobx store
            const topic = this.mapViewerConfig.getTopicByRoute(`/${topicRoute}`, this.mapViewerConfig.subtopics);
            if(topic) {
                this.appStore.setSubtopic(topic);
            }
        }
    }

    setTopic(subtopic?: TopicConfiguration | null): void {
        this.oStore.resetSelect();
        this.appStore.setSubtopic(subtopic);
        if (!subtopic){
            this.appStore.setPageTitle("objekt.lu.ch: Informationssystem Objektwesen");
            // quick and dirty!
            if (typeof this.mapViewerConfig.htmlContent.mainPage === "string") {
                const topContent: React.ReactElement[] = [];
                if (this.appConfig.mode !== "production"
                    && typeof this.mapViewerConfig.htmlContent.betaInfo === "string") {
                    topContent.push(<div key={"test-banner"} className="flash">
                        <div className={"alert alert-error"}>
                            <p dangerouslySetInnerHTML={
                                {__html: this.mapViewerConfig.htmlContent.betaInfo}} />
                        </div>
                    </div>);
                }
                topContent.push(
                    <div key={"main-page"} dangerouslySetInnerHTML={
                        {__html: this.mapViewerConfig.htmlContent.mainPage}} className={"geo-top-content"}/>);
                this.appStore.setTopContent(topContent);
            }
            if (typeof this.mapViewerConfig.htmlContent.mainPage2 === "string") {
                const bottomContent = (
                    <div dangerouslySetInnerHTML={{__html: this.mapViewerConfig.htmlContent.mainPage2}}/>
                );
                this.appStore.setBottomContent(bottomContent);
            }
            this.appStore.setBreadcrumbs(new NavigationConfiguration());
            this.appStore.setAsideContent(null);
            document.title = this.mapViewerConfig.appName;
            this.oStore.setShowMap(true);
        } else {
            this.appStore.setTopContent(null);
            this.appStore.setBottomContent(null);
        }
    }

    /**
     * Extract station ID from url
     * Select station when station ID is present
     */
    selectObjectFromUrl(): void {
        const {params} = this.props;
        if (!params || !params.objectId) {
            return;
        }

        const objectId = params.objectId;
        this.appStore.setObjectId(objectId);
        if (this.appStore.subtopicId === "grundstueck") {
            this.oStore.requestGrundstueck(objectId);
        } else if (this.appStore.subtopicId === "gebaeude") {
            this.oStore.requestGebaeude(objectId, this.props.params.addId);
        } else if (this.appStore.subtopicId === "bauprojekt") {
            this.oStore.requestProjekt(objectId);
        }
    }
    goToDefaultExtent(): void {
        if(!this.mapStore.view){
            return;
        }
        this.mapStore.view.goTo({
            target: new Viewpoint(this.appConfig.mapView.viewpoint),
            scale: this.defaultScale}).catch((error) => {
            if (error.name !== "view:goto-interrupted") {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        });
    }
    handleResize(): void {
        this.checkUiSize();
    }
    checkUiSize(): void {
        const uiHeight = this.appUiRef.current?.clientHeight;
        const uiWidth = this.appUiRef.current?.clientWidth;
        const isSmallDevice = (window.innerWidth <= this.mapViewerConfig.breakpoints.md);
        this.uiStore.setUiSize(uiWidth, uiHeight, isSmallDevice);
    }
    componentDidMount(): void {
        const { location, navigate, params } = this.props;

        document.title = this.mapViewerConfig.appName;
        // store route information to mobx store
        this.appStore.setRoute(this.props.location, this.props.params, this.props.navigate);
        this.setTopicFromUrl();
        this.selectObjectFromUrl();

        this.mapStore.setWebMap(this.mapViewerConfig.webMap);

        // rerun until navigate is available
        autorun((navigationReaction) => {
            const appStoreLocation = this.appStore.location;
            if (!appStoreLocation) {
                return;
            }
            this.handleBrowserNavigation(location, navigate, params);
            this.checkUiSize();
            navigationReaction.dispose(); // stop autorun
        });
        autorun((searchReaction) => {
            if (!this.mapViewerStore.SearchStore.viewReady.get("search")) {
                return;
            }
            this.checkUiSize();
            this.prepareSearch(this.mapViewerStore.SearchStore.getViewModel("search"));
            searchReaction.dispose(); // stop autorun
        });
        this.checkUiSize();
        window.addEventListener("resize", this.handleResize);
        this.updateMessages();
        this.autorunDisposers.push(reaction(() => this.mapStore.activeLayers, () => {
            this.updateMessages();
        }));
        autorun(() => {
            if (!this.mapViewerStore.SearchStore.viewReady.get("search-2")) {
                return;
            }
            // reaction.dispose(); // stop autorun
            this.prepareSearch(this.mapViewerStore.SearchStore.getViewModel("search-2"));
        });
    }
    prepareSearch(searchViewModel: ExtendedSearchViewModel | undefined): void {
        if(!searchViewModel){
            return;
        }
        if (searchViewModel.hasEventListener("suggestion-selected")) {
            return;
        }
        searchViewModel.on( "suggestion-selected", () => {
            this.oStore.setShowMap(true);
        });
        searchViewModel.on( "result-found", (event: any) => {
            if (event.feature.attributes.egrid) {
                this.oStore.requestGrundstueck(event.feature.attributes.egrid);
            } else if (event.feature.attributes.bagenr) {
                this.oStore.requestProjekt(event.feature.attributes.bagenr);
            } else if (event.feature.attributes.egid) {
                let edid: number | undefined;
                if (event.feature.attributes.hasOwnProperty("edid")) {
                    edid = parseInt(event.feature.attributes.edid, 10);
                }
                this.oStore.requestGebaeude(event.feature.attributes.egid, edid);
            }
        });
        (searchViewModel as any).on( "result-detail-found", (event: any) => {
            // Resultat von Gebäudeversicherungsnummern anzeigen
            if (event.sourceConfig.categories.includes("gebaudeversicherungsnumer")) {
                if (event.response.features.length > 0) {
                    const feature = event.response.features[0];
                    if (feature.attributes.hasOwnProperty("gwr_egid")) {
                        this.oStore.requestGebaeude(feature.attributes.gwr_egid, undefined);
                    }
                }
            }
        });
    }
    componentDidUpdate(prevProps: AppProps): void {
        const { location, navigate, params } = this.props;
        if(prevProps.location.pathname !== this.props.location.pathname
            || (this.props.location.hash && prevProps.location.hash!==this.props.location.hash)){
            this.appStore = this.props.appStore;
            this.handleBrowserNavigation(location, navigate, params);
        }
    }
    componentWillUnmount(): void {
        // dispose reactions
        this.stopBrowserNavigationListener();
        window.removeEventListener("resize", this.handleResize);
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
    }

    setSidebarVisible(visible: boolean): void {
        this.uiStore.setSidebarVisible(visible);
    }

    render(): React.ReactElement | null {
        let mapProperties: __esri.WebMapProperties | undefined;
        const messages: React.ReactElement[] = [];
        const {maintenance} = this.appConfig;
        const {sidebarVisible} = this.uiStore;
        const {status} = this.mapStore;
        const {pageTitle, topContent, bottomContent, subtopic, tracker} = this.appStore;
        const {showMap, selectedObjektType} = this.oStore;
        const {northFacing} = this.state;
        const subtopicId = (subtopic? subtopic.id: "");
        if (this.mapStore.webMap){
            mapProperties = this.mapStore.webMap as __esri.WebMapProperties;
            if (this.mapViewerConfig.meta && Array.isArray(this.mapViewerConfig.meta.alerts)) {
                this.mapViewerConfig.meta.alerts.forEach((message, index) => {
                    // quick and dirty!
                    const alertContent = <p dangerouslySetInnerHTML={{__html: message.content}} />;
                    messages.push(
                        <MessageBlock
                            key={index}
                            index={index}
                            topic={"objekt.lu"}
                            header={message.title}
                            className={message.class? message.class: undefined}
                            content={alertContent}
                            appConfig={this.appConfig}/>
                    );
                });
            }
        }
        let newBottomContent: React.ReactElement | null = null;
        if (pageTitle) {
            if (subtopicId === "") {
                document.title = this.mapViewerConfig.appName + " - Kanton Luzern";
                newBottomContent = (
                    <Item.Group>
                        <Item>
                            <Item.Content>
                                <Item.Header as={"h3"}>Nach Objekten suchen</Item.Header>
                                <Search
                                    keyAppend={"2"}
                                    mapStore={this.mapStore}
                                    uiStore={this.uiStore}
                                    config={this.appConfig.search}
                                    tracker={tracker}/>
                            </Item.Content>
                        </Item>
                        <Item>
                            <Item.Content>
                                {bottomContent}
                            </Item.Content>
                        </Item>
                    </Item.Group>
                );
            } else {
                document.title = pageTitle + " - " + this.mapViewerConfig.appName;
            }
        }
        let pageContent: React.ReactElement;
        if (subtopicId === "kontakt" && !selectedObjektType) {
            pageContent = (<Kontakt />);
        } else if (subtopicId === "faq" && !selectedObjektType) {
            pageContent = (<Faq view={this.mapStore.view} />);
        } else if (subtopicId === "ueber" && !selectedObjektType) {
            pageContent = (<Ueber />);
        } else if (subtopicId === "aktuell" && !selectedObjektType) {
          pageContent = (<Aktuell />);
        } else {
            pageContent = (<ObjectResult
                key={this.appStore.topic.id+subtopicId}
            />);
        }
        let addClass = "geo-app-body";
        addClass += (!showMap? " hide": "");
        addClass += (this.appStore.topic)? " topic-" + this.appStore.topic.id : "";
        addClass += (this.appStore.subtopic)? " subtopic-" + this.appStore.subtopic.id : "";
        addClass += " " + status;
        addClass += (northFacing? " geo-north-facing": " geo-not-north-facing");
        const mainContent: React.ReactElement = (
            <div className="mid-content-column">
                <h2 id={"geo-page-title"}>{pageTitle}</h2>
                {topContent}
                <div id="map-root" >
                    <div className={addClass}>
                        <React.Suspense fallback={
                            <div className="pre-loader">
                                <div className="pre-loader-inner">Anwendung wird geladen ...
                                </div>
                            </div>
                        }>
                            <div className="pre-loader">
                                <div className="pre-loader-inner">Anwendung wird geladen ...</div>
                            </div>
                            <WebMap
                                mapProperties={mapProperties}
                                viewProperties={
                                    this.appConfig.mapView as __esri.MapViewProperties}
                                onLoad={this.handleMapLoad}
                                store={this.mapStore}
                                uiStore={this.uiStore}>
                            </WebMap>
                        </React.Suspense>
                        <div className={"geo-app"} ref={this.appUiRef}>
                            <Maintenance config={maintenance} uiStore={this.uiStore} />
                            <div className={"geo-ui-root"}>
                                <div className={"geo-ui"}>
                                    <div className={
                                        "geo-ui-inner-container geo-ui-manual-container"}>
                                        <Popup config={this.mapViewerConfig.popup}
                                            mapViewerConfig={this.mapViewerConfig}
                                            appConfig={this.appConfig}
                                            view={this.mapStore.view}
                                            uiStore={this.uiStore}
                                            appStore={this.appStore}
                                            mapStore={this.mapStore}/>
                                    </div>
                                    <div className={
                                        "geo-ui-inner-container geo-ui-corner-container"}>
                                        <div className={"geo-ui-top-left geo-ui-corner"}/>
                                        <div className={"geo-ui-top-right geo-ui-corner"}/>
                                        <div className={"geo-ui-bottom-right geo-ui-corner"}/>
                                        <div className={"geo-ui-bottom-left geo-ui-corner"}>
                                            {messages.length > 0 && <div className="geo-messages">{messages}</div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Footer view={this.mapStore.view}
                                appStore={this.appStore}
                                appConfig={this.appConfig}
                                uiStore={this.uiStore}
                                mapStore={this.mapStore}
                                mapViewerConfig={this.mapViewerConfig} />
                        </div>
                        <MessageBox store={this.appStore}/>
                    </div>
                </div>
                {pageContent}
                {(newBottomContent)? newBottomContent: bottomContent}
            </div>
        );
        return(
            <ErrorBoundary>
                <AppStoreProvider store={this.mapViewerStore}>
                    <SemanticSidebar.Pushable>
                        <SemanticSidebar
                            animation="push"
                            onHide={() => this.setSidebarVisible(false)}
                            visible={sidebarVisible}
                        >
                            <MobileNavigation
                                config={this.mapViewerConfig.navigation}
                                uiStore={this.uiStore}
                                handleNavigation={this.handleNavigation}/>
                        </SemanticSidebar>

                        <SemanticSidebar.Pusher>
                            <div className="page">
                                <Header view={this.mapStore.view}
                                    appConfig={this.appConfig}
                                    appStore={this.appStore}
                                    uiStore={this.uiStore}
                                    mapStore={this.mapStore}
                                    capabilities={["search"]}
                                    searchConfig={this.appConfig.search}
                                    mapViewerConfig={this.mapViewerConfig}
                                />
                                <div id="layout" className="wrapper container layout">
                                    <Navigation config={this.mapViewerConfig.navigation}
                                        handleNavigation={this.handleNavigation}
                                        appConfig={this.appConfig}
                                        appStore={this.appStore}
                                        uiStore={this.uiStore}/>
                                    <main className="layout__main">
                                        <Breadcrumbs config={this.mapViewerConfig.breadcrumbs}
                                            handleNavigation={this.handleNavigation}
                                            uiStore={this.uiStore}
                                            appStore={this.appStore}
                                            appConfig={this.appConfig}/>
                                        <div className="content one-col">
                                            {mainContent}
                                        </div>
                                    </main>
                                    <Aside view={this.mapStore.view}/>
                                </div>
                                <ObjektwesenFooter view={this.mapStore.view}
                                    appStore={this.appStore}
                                    appConfig={this.appConfig}
                                    uiStore={this.uiStore}
                                    mapStore={this.mapStore}
                                    handleNavigation={this.handleNavigation}/>
                            </div>
                        </SemanticSidebar.Pusher>
                    </SemanticSidebar.Pushable>
                    <CookieBanner mapViewerConfig={this.mapViewerConfig}/>
                </AppStoreProvider>
            </ErrorBoundary>
        );
    }

    handleMapLoad(map: __esri.Map, view: __esri.MapView | __esri.SceneView): void {
        // TODO: AppState status erst auf complete schalten, wenn alle Widgets geladen sind
        // this.appStore.setState({ status: "complete" });
        if (this.mapViewerConfig.locateButtonEnabled) {
            const locateWidget = new EsriLocate({
                view
            });
            view.ui.add(locateWidget, "top-right");
        }
        // mode zoom buttons to top right
        view.ui.move("zoom", "top-right");
        const compassWidget = new EsriCompass({
            view
        });
        view.ui.add(compassWidget, "top-right");
        view.watch("rotation", (newValue: number)=>{
            this.setState({northFacing: newValue === 0});
        });

        view.closePopup();
    }
    handleNavigation(route: string): void {
        // remove basename
        if (this.appConfig.basename.length > 1) {
            route = route.substring(this.appConfig.basename.length - 1);
        }
        runInAction(()=> {
            this.appStore.setRoutePath(route);
        });
        const topic = this.mapViewerConfig.getTopicByRoute("/" + route.split("/")[1],
            this.mapViewerConfig.subtopics);
        this.oStore.resetSelect();
        document.getElementById("geo-page-title")?.scrollIntoView({behavior: "smooth"});
        if (topic?.id === "objektwesen") {
            this.setTopic();
            // Popup schliessen und auf ursprünglichen Extent zoomen
            this.mapViewerStore.PopupStore.setVisible(false);
            this.goToDefaultExtent();
        } else {
            this.setTopic(topic);
        }
        // this.selectObjectFromUrl(topic);
    }
    /**
     * Register listener to set topic, when navigation is done with browser back/forward buttons
     */
    handleBrowserNavigation(location: Location, navigate: NavigateFunction,params: any): void {
        // store route information to mobx store
        runInAction(()=> {
            this.appStore.setRoute(location, params, navigate);
            this.handleSubtopicBrowserNavigation();
        });
    }
    handleSubtopicBrowserNavigation(): void {
        const { params } = this.props;
        if(!params.subtopic) {
            this.oStore.resetSelect();
            this.appStore.setSubtopic();
            this.setTopic();
        }else if(!this.appStore.subtopic || !this.appStore.subtopic.routes.includes("/"+params.subtopic)){
            const subtopic = this.mapViewerConfig.getTopicByRoute(
                "/"+params.subtopic, this.mapViewerConfig.subtopics);
            if(subtopic) {
                this.appStore.setSubtopic(subtopic);
            }
        }
        if(params.objectId !== this.appStore.objectId){
            this.appStore.setObjectId(this.props.params.objectId);
            this.oStore.resetSelect();
            this.selectObjectFromUrl();
        }
    }
    updateMessages(){
        const startupMessage = this.prepareMessagesContent(
            this.mapViewerConfig.meta?.startupMessage?
                [this.mapViewerConfig.meta.startupMessage]: undefined, true);
        Promise.all(startupMessage).then((msgs) => {
            this.setState({startupMessage: msgs.filter((m) => m)});
        });
        const alertMessages = this.prepareMessagesContent(this.mapViewerConfig.meta.alerts);
        Promise.all(alertMessages).then((msgs) => {
            this.setState({alertMessages: msgs.filter((m) => m)});
        });
    }
    prepareMessagesContent(messagesConfig?: MessageConfiguration[], startup=false):
    Array<Promise<React.ReactElement | undefined>> {
        const messagePromises: Array<Promise<React.ReactElement | undefined>> = [];
        if (messagesConfig && Array.isArray(messagesConfig)) {
            const profile: __esri.Profile = {
                variables: [{
                    name: "$subtopicId",
                    type: "text"
                }, {
                    name: "$activeLayers",
                    type: "array",
                    elementType: {
                        type: "text"
                    }
                }]
            };
            messagesConfig.forEach((message, index) => {
                if (typeof message.condition === "undefined") {
                    messagePromises.push(new Promise((resolve) => {
                        resolve(this.prepareMessageContent(message, index, startup));
                    }));
                } else {
                    if(this.mapStore.activeLayers) {
                        const arcadeExecutor = arcade.createArcadeExecutor(message.condition, profile);
                        messagePromises.push(new Promise((resolve) => {
                            arcadeExecutor.then((ae) => {
                                const condition = ae.execute({
                                    $activeLayers: this.mapStore.activeLayers,
                                    $subtopicId: (this.appStore.subtopicId? this.appStore.subtopicId: "")
                                });
                                let m: React.ReactElement | undefined;
                                if (condition) {
                                    m = this.prepareMessageContent(message, index, startup);
                                }
                                resolve(m);
                            });
                        }));
                    }
                }
            });
        }
        return messagePromises;
    }
    prepareMessageContent(messageConfig: MessageConfiguration, index: number, startup=false): React.ReactElement {
        let message: React.ReactElement;
        if (startup){
            message = <MessageStartup
                key={index}
                topic={this.appStore.topic.id}
                header={messageConfig.title}
                className={messageConfig.class ? messageConfig.class : ""}
                content={messageConfig.content}
                appConfig={this.appConfig}/>;
        } else {
            message = <MessageBlock
                key={index}
                index={index}
                topic={this.appStore.topic.id}
                header={messageConfig.title}
                className={messageConfig.class ? (messageConfig as MessageProps).class : undefined}
                content={messageConfig.content}
                appConfig={this.appConfig}/>;
        }
        return message;
    }
}
